import type {
  PlayerConfig,
  PlayerSources,
  PluginsConfig,
  SettingsConfig,
  WatermarkConfig,
} from '@movingimage-evp/player-library';
import type { ChaptersConfig } from '@movingimage-evp/player-library/dist/components/player/player.config';
import type { VodChapter } from 'src/generated/graphql-viewer';

import type { ECdnSettings, HiveToken } from '../../../generated/graphql-viewer';
import type { UserInfoJwt } from '../../../utils/parse-jwt';

import * as FingerprintJS from '@fingerprintjs/fingerprintjs';
import { SourceType } from '@movingimage-evp/player-library';
import { hash } from 'hash-it';
import { useEffect, useState } from 'react';

import { devLog, expiresAt } from '../../../utils/dev-log';
import { parseJwt } from '../../../utils/parse-jwt';
import { getSessionStorageItem } from '../../../utils/session-storage';
import { getUserInformation } from '../../../utils/user-information-utils';

export type LsproPlayerConfig = {
  webcastId: string;
  contentId?: string;
  playbackUrl?: string;
  title?: string;
  accountId?: string;
  lsproId?: string;
  primaryColor?: string;
  iconColor?: string;
  logoUrl?: string;
  hiveConfiguration?: HiveToken | null;
  vBrickConfiguration?: ECdnSettings['vBrick'];
  kollectiveConfiguration?: ECdnSettings['kollective'];
  state?: string;
  channelName?: string;
  channelId?: string;
  videoManagerId?: string;
  groupId?: string;
  language?: string;
  primaryLanguage?: string;
  muted?: boolean;
  autoplay?: boolean;
  hidePlaybackSpeed?: boolean;
  watermarkContent?: string;
  chapters?: VodChapter[];
};

export function usePlayerConfig(config?: LsproPlayerConfig): PlayerConfig | undefined {
  const [playerConfig, setPlayerConfig] = useState<PlayerConfig | undefined>();

  useEffect(() => {
    if (!config) return;

    const { playbackUrl } = config;

    if (!playbackUrl) return;

    const updatePlayerConfig = async () => {
      let visitorId = '';

      try {
        visitorId = await fetchVisitorId(config.webcastId);
      } finally {
        setPlayerConfig(getPlayerConfig({ ...config, visitorId, playbackUrl }));
      }
    };

    updatePlayerConfig();
  }, [config]);

  return playerConfig;
}

// TODO: Temporary - LSPRO-3290
const customHostLsproIds = [
  'rsmfTMvaZUG1ytj5ntR6r', // Union Investment Gruppe - Test
  'u5UWFvnw23QhKWcLLvV73', // Union Investment
  'U6CsTtwRBPX3YwiOOgEAv', // Deka
  'WebCats', // Our QA environment
];

function getPlugins({
  webcastId,
  contentId,
  title,
  visitorId,
  accountId,
  lsproId,
  hiveConfiguration,
  vBrickConfiguration,
  kollectiveConfiguration,
  state,
  channelName,
  channelId,
  videoManagerId,
  groupId,
  language,
}: LsproPlayerConfig & { visitorId: string }): PluginsConfig {
  const youboraAnalytics = {
    accountCode: process.env.REACT_APP_YOUBORA_ACCOUNT_CODE || '',
    host: customHostLsproIds.includes(lsproId || '')
      ? `${window.location.origin}/${process.env.REACT_APP_YOUBORA_HOST}`
      : undefined,
    'user.name': visitorId,
    'content.title': title || '',
    'content.id': contentId || '',
    'content.channel': channelName || '',
    'content.language': language || '',
    'content.customDimension.1': videoManagerId || '',
    'content.customDimension.3': channelId || '',
    'content.customDimension.6': groupId || '',
    'content.customDimension.7': accountId || '',
    'content.customDimension.8': 'LiveStream Pro',
    'content.customDimension.9': state,
    'content.customDimension.10': webcastId,
    'content.customDimension.11': lsproId,
  };

  const hive = hiveConfiguration
    ? {
        hive: {
          eventName: title,
          token: {
            videoId: webcastId,
            jwt: hiveConfiguration.jwt || '',
            manifest: hiveConfiguration.manifest || '',
          },
        },
      }
    : {};

  const vBrick = vBrickConfiguration
    ? {
        ramp: {
          id: webcastId,
          title: title,
          vbrickApiKey: vBrickConfiguration.vbrickApiKey || '',
          vbrickApiSecret: vBrickConfiguration.vbrickApiSecret || '',
          vbrickHostName: vBrickConfiguration.vbrickHostName || '',
          vbrickJwtKeyName: vBrickConfiguration.vbrickJwtKeyName || '',
          omnicacheUris: vBrickConfiguration.vBrickOmnicacheUris
            ? vBrickConfiguration.vBrickOmnicacheUris.join(',')
            : '',
        },
      }
    : {};

  const kollective = kollectiveConfiguration
    ? {
        kollective: {
          jitc: kollectiveConfiguration.jitc || '',
          integratorToken: kollectiveConfiguration.jwt || '',
          userToken: visitorId,
        },
      }
    : {};

  if (kollectiveConfiguration) {
    const integratorToken = parseJwt<{ exp: number }>(kollectiveConfiguration.jwt || '');

    if (integratorToken) {
      devLog(
        'Kollective',
        kollective.kollective,
        '\n integratorToken expires',
        expiresAt(integratorToken.exp * 1000 - Date.now())
      );
    }
  }

  return {
    youboraAnalytics,
    ...hive,
    ...vBrick,
    ...kollective,
  };
}

function getSources(src: string): PlayerSources {
  return {
    hls: {
      src,
      type: SourceType.HLS,
    },
  };
}

function getSettings(
  primaryColor?: string,
  iconColor?: string,
  logoUrl?: string,
  muted?: boolean,
  autoplay?: boolean,
  hidePlaybackSpeed?: boolean
): SettingsConfig {
  return {
    liveUI: true,
    hideLoopButton: true,
    primaryColor,
    iconColor,
    logoUrl,
    maxPlaylistRetries: 0,
    autoplay,
    videoLayout: 'fill',
    muted,
    hidePlaybackSpeed,
  };
}

function getWatermark(content?: string): WatermarkConfig {
  return {
    content: content || '',
  };
}

function getChapters(chapters?: VodChapter[], language?: string, primaryLanguage?: string): ChaptersConfig | undefined {
  if (!chapters || !chapters.length || !language) return undefined;

  return {
    [language]: chapters.map(({ offsetInMs, titles, thumbnailUrl }) => {
      const translatedTitle = titles.find((title) => title.language === language)?.text;
      const fallbackTitle = titles.find((title) => title.language === primaryLanguage)?.text;

      return {
        start: offsetInMs,
        title: translatedTitle || fallbackTitle || '',
        thumbnail: thumbnailUrl || '',
      };
    }),
  };
}

// Generates a hashed visitor ID based on SSO ID, registration email or fingerprint
async function fetchVisitorId(eventId: string): Promise<string> {
  const { email } = getUserInformation(eventId) || {};
  const jwt = parseJwt<UserInfoJwt>(getSessionStorageItem('viewerAccessToken'));
  const ssoId = jwt ? jwt.employeeId + ':' + jwt.email : undefined;

  let id = '';
  if (ssoId) id = ssoId;
  else if (email) id = email;
  else id = (await (await FingerprintJS.load()).get()).visitorId;

  return hash(eventId + ':' + id).toString();
}

function getPlayerConfig(config: LsproPlayerConfig & { visitorId: string; playbackUrl: string }): PlayerConfig {
  return {
    sources: getSources(config.playbackUrl),
    settings: getSettings(
      config.primaryColor,
      config.iconColor,
      config.logoUrl,
      config.muted,
      config.autoplay,
      config.hidePlaybackSpeed
    ),
    metadata: { title: config.title },
    watermark: getWatermark(config.watermarkContent),
    plugins: getPlugins(config),
    chapters: getChapters(config.chapters, config.language, config.primaryLanguage),
    lng: config.language,
    analyticsMode: 'full',
  };
}
